import React from "react";
import { FaClock, FaFileAlt, FaCamera, FaTasks } from "react-icons/fa";
import { useT } from "../contexts/TranslationContext";

/**
 * ProjectStats - Displays project statistics with icons
 * @param {Object} stats - Statistics object with totalHours, reportCount, photoCount, taskProgress
 * @param {string} variant - Display variant: 'compact' (default) or 'detailed'
 */
const ProjectStats = ({ stats, variant = "compact" }) => {
  const { t } = useT();
  if (!stats) return null;

  const { totalHours, reportCount, photoCount, taskProgress } = stats;

  const scrollToTimeReports = () => {
    const timeReportsSection = document.getElementById("time-reports-section");
    if (timeReportsSection) {
      timeReportsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToPhotos = () => {
    const photosSection = document.getElementById("photos-section");
    if (photosSection) {
      photosSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTasks = () => {
    const tasksSection = document.getElementById("tasks-section");
    if (tasksSection) {
      tasksSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const statItems = [
    {
      icon: FaClock,
      labelKey: "projects.statsHours",
      value: totalHours ? Math.round(totalHours) : 0,
      show: totalHours !== undefined,
      clickable: true,
      onClick: scrollToTimeReports,
    },
    {
      icon: FaFileAlt,
      labelKey: "projects.statsReports",
      value: reportCount || 0,
      show: reportCount !== undefined,
      clickable: true,
      onClick: scrollToTimeReports,
    },
    {
      icon: FaCamera,
      labelKey: "projects.statsPhotos",
      value: photoCount || 0,
      show: photoCount !== undefined,
      clickable: true,
      onClick: scrollToPhotos,
    },
    {
      icon: FaTasks,
      labelKey: "projects.statsTasks",
      value: taskProgress !== null && taskProgress !== undefined ? `${Math.round(taskProgress)}%` : null,
      show: taskProgress !== null && taskProgress !== undefined,
      clickable: true,
      onClick: scrollToTasks,
    },
  ].filter((item) => item.show);

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-4 flex-wrap">
        {statItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} className="flex items-center gap-1.5 text-sm text-gray-600">
              <Icon className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{item.value}</span>
              <span className="text-gray-500">{t(item.labelKey)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Detailed variant
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {statItems.map((item, idx) => {
        const Icon = item.icon;
        const isClickable = item.clickable && item.onClick;
        return (
          <div
            key={idx}
            className={`flex items-center gap-2 p-2 bg-gray-50 rounded ${
              isClickable
                ? "cursor-pointer hover:bg-gray-100 transition-colors"
                : ""
            }`}
            onClick={isClickable ? item.onClick : undefined}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={
              isClickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.onClick();
                    }
                  }
                : undefined
            }
          >
            <Icon className="w-5 h-5 text-gray-600" />
            <div>
              <div className="text-sm font-semibold text-gray-900">{item.value}</div>
              <div className="text-xs text-gray-500">{t(item.labelKey)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectStats;
